import Icon from '@/components/icons/Icon';
import { classnames, isEmpty } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { __, _s } from '@/locale';
import { Fab } from '../../forms/buttons';
import Label from '../../Label/Label';
import Card from '../Card';
import {
  getFormattedDiscountedPrice,
  getFormattedPrice,
  getPriceLabel,
  getValidPriceTypeForDiscount,
} from './ServiceCard.helper';
import { ServiceCardProps } from './ServiceCard.types';

const MIDDLE_DOT_STYLE =
  'before:w-xxs before:h-xxs before:mr-2xs before:bg-fg_disabled_light before:rounded-full before:content-[""] before:self-center';

const ServiceCard = ({
  name,
  highlightedName,
  subtTitle,
  id,
  bundle,
  employeeName,
  description,
  duration,
  price,
  discount = {},
  onShowReadMore,
  firstAvailableTime,
  onRowClick,
  rightSlot,
  leftSlot,
  tags = {},
  readMore,
  timeslot = {},
}: ServiceCardProps) => {
  const validPriceTypeForDiscount = getValidPriceTypeForDiscount(price, discount.amounts);
  const hasLabels = tags.wellnessCard || discount.percentage || tags.waitingList || !isEmpty(timeslot);
  const hasDiscounts = Boolean(discount.amounts?.length) || (Boolean(discount.validity) && price?.type === 'variable');
  const { isMobileView } = useMobileView();

  const originalFormattedPrice = price
    ? getFormattedPrice({
        type: price?.type,
        value: price?.value,
        isMobileView: isMobileView,
        hasDiscounts: hasDiscounts && validPriceTypeForDiscount,
      })
    : undefined;

  const discountedFormattedPrice = getFormattedDiscountedPrice(
    hasDiscounts,
    price,
    discount.amounts ?? [],
    isMobileView,
  );

  const handleShowReadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onShowReadMore(name, description, id);
  };

  const CtaSection = () => {
    const hasValidPriceOrDiscount = price && (price.type !== 'variable' || hasDiscounts);
    const hasCtaServiceInfo = (isMobileView && (duration || bundle?.quantity)) || hasValidPriceOrDiscount;
    return (
      <div
        className={classnames(
          `border-border_regular`,
          isMobileView && (hasValidPriceOrDiscount || duration) && 'mt-sm border-t',
          !isMobileView && hasValidPriceOrDiscount && 'border-l',
        )}>
        <div
          className={classnames(
            'text-s flex h-full items-center',
            isMobileView && hasCtaServiceInfo ? 'pt-sm gap-sm justify-between' : 'pl-sm gap-md py-xs justify-end',
            !isMobileView && price && price.type !== 'variable' && 'min-w-[170px]',
          )}>
          {hasCtaServiceInfo && (
            <div className="gap-2xs flex flex-col justify-center">
              {hasValidPriceOrDiscount && (
                <div
                  className={classnames(
                    'flex',
                    isMobileView ? 'gap-xs items-center' : 'gap-2xs flex-col items-end justify-center',
                  )}>
                  {!isMobileView && (!hasDiscounts || price.type === 'variable') && (
                    <span className="text-s text-fg_secondary w-full text-right">
                      {getPriceLabel(price?.type, hasDiscounts)}
                    </span>
                  )}
                  {hasDiscounts && isMobileView && discountedFormattedPrice && (
                    <span className="text-m text-fg_highlight font-bold">{discountedFormattedPrice}</span>
                  )}
                  {originalFormattedPrice && (
                    <span
                      className={classnames(
                        hasDiscounts && discountedFormattedPrice
                          ? 'text-fg_secondary line-through'
                          : 'text-m font-bold',
                      )}>
                      {`${price.type === 'from' && isMobileView ? 'Fr. ' : ''}${originalFormattedPrice}`}
                    </span>
                  )}
                  {hasDiscounts && !isMobileView && discountedFormattedPrice && (
                    <span className="text-m text-fg_highlight font-bold">{discountedFormattedPrice}</span>
                  )}
                </div>
              )}

              {isMobileView && (duration || bundle?.quantity) && (
                <div className="flex">
                  {duration && <span className="text-fg_secondary whitespace-nowrap font-bold">{duration}</span>}
                  {firstAvailableTime && <span className={`text-s text-fg_link ml-xs`}>{firstAvailableTime}</span>}
                  {bundle?.quantity && (
                    <span
                      className={`text-s text-fg_secondary ml-2xs flex font-bold ${
                        duration ? MIDDLE_DOT_STYLE : ''
                      }`}>{`${_s('buyBundles.quantity', {
                      count: bundle.quantity,
                    })}`}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {rightSlot && rightSlot}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card size="sm" childClassName="px-lg">
        <div
          onClick={onRowClick}
          role={onRowClick ? 'button' : ''}
          className={classnames(
            `flex`,
            isMobileView && 'flex-col',
            price && price.type !== 'variable' && !isMobileView ? 'gap-sm' : '',
          )}>
          <div className={`flex min-w-0 flex-1 items-center justify-between`}>
            <div className={`gap-sm flex flex-1 overflow-hidden ${hasLabels ? 'items-start' : 'items-center'}`}>
              {!isMobileView && leftSlot}
              <div className="gap-xs flex min-w-0 flex-1 flex-col">
                <div className="gap-sm flex justify-between">
                  <div className="gap-sm flex min-w-0 flex-1">
                    {isMobileView && leftSlot}
                    <div
                      className={`gap-xs flex min-w-0 flex-1 flex-col overflow-hidden ${
                        isMobileView ? 'justify-center' : ''
                      }`}>
                      <h2
                        className={`text-l text-fg_primary ${isMobileView ? 'line-clamp-2' : 'truncate'}`}
                        style={!isMobileView ? { maxWidth: '100%' } : undefined}>
                        {highlightedName ?? name}
                      </h2>
                      {employeeName && <span className="text-m text-fg_secondary">{employeeName}</span>}

                      {!isMobileView && (duration || bundle || discount?.validity || subtTitle) && (
                        <div className="text-s gap-2xs flex items-center">
                          {hasLabels && !isMobileView && (
                            <div className="gap-xs flex flex-wrap">
                              {timeslot?.fullyBooked && <Label variant="fully-booked" size="lg" />}
                              {timeslot && !timeslot.fullyBooked && timeslot.capacity && timeslot.capacity <= 3 && (
                                <Label
                                  variant="spotsLeft"
                                  size="lg"
                                  label={_s(
                                    `${timeslot.capacity === 1 ? 'availableCapacity_one' : 'availableCapacity_other'}`,
                                    {
                                      count: timeslot.capacity,
                                    },
                                  )}
                                />
                              )}
                              {timeslot && !timeslot.fullyBooked && timeslot.capacity > 3 && (
                                <Label variant="available" size="lg" />
                              )}
                              {timeslot?.from && <Label variant="time" size="lg" label={timeslot.from} />}
                              {tags?.waitingList && <Label variant="fully-booked" label={__('fullBookedWaitlist')} />}
                              {tags?.wellnessCard && <Label variant="wellness" size="lg" />}
                              {discount.percentage && (
                                <Label variant="smt" label={`-${discount.percentage}%`} icon={{ variant: 'smt' }} />
                              )}
                            </div>
                          )}
                          {(duration || bundle || discount?.validity) && (
                            <div className={`flex items-center ${readMore ? 'w-full' : ''}`}>
                              {!isMobileView && duration && (
                                <span className="text-fg_secondary whitespace-nowrap font-bold">{duration}</span>
                              )}
                              {firstAvailableTime && (
                                <span
                                  className={`text-s text-fg_link ml-2xs ${
                                    duration ? `${MIDDLE_DOT_STYLE} flex` : ''
                                  }`}>
                                  {firstAvailableTime}
                                </span>
                              )}
                              {bundle?.quantity && !isMobileView && (
                                <span
                                  className={`text-s text-fg_secondary ml-2xs my-1 ${
                                    duration || firstAvailableTime ? `${MIDDLE_DOT_STYLE} flex` : ''
                                  }`}>{`${_s('buyBundles.quantity', {
                                  count: bundle.quantity,
                                })}`}</span>
                              )}
                              {(bundle?.validity || discount?.validity) && (
                                <span
                                  className={`text-fg_secondary text-s whitespace-nowrap ${
                                    isMobileView ? '' : 'ml-2xs my-1'
                                  } ${
                                    (bundle?.quantity || duration) && !isMobileView ? `${MIDDLE_DOT_STYLE} flex` : ''
                                  }`}>
                                  {bundle?.validity ?? discount?.validity}
                                </span>
                              )}
                            </div>
                          )}
                          {!isMobileView && subtTitle}
                        </div>
                      )}
                    </div>
                    {readMore && isMobileView && <div className="flex flex-shrink-0 items-center">{readMore}</div>}
                  </div>

                  {!isMobileView && readMore}

                  {description && onShowReadMore && (
                    <div className={`text-s text-fg_link flex flex-shrink-0 items-center`}>
                      <Fab
                        size="xs"
                        onClick={handleShowReadMore}
                        variant="borderless"
                        icon={<Icon variant="info-circle" color="fg_link" />}
                      />
                    </div>
                  )}
                </div>

                {hasLabels && isMobileView && (
                  <div className="gap-sm flex flex-wrap">
                    {timeslot?.fullyBooked && <Label variant="fully-booked" size="lg" />}
                    {timeslot && !timeslot.fullyBooked && timeslot.capacity && timeslot.capacity <= 3 && (
                      <Label
                        variant="spotsLeft"
                        size="lg"
                        label={_s(`${timeslot.capacity === 1 ? 'availableCapacity_one' : 'availableCapacity_other'}`, {
                          count: timeslot.capacity,
                        })}
                      />
                    )}
                    {timeslot && !timeslot.fullyBooked && timeslot.capacity > 3 && (
                      <Label variant="available" size="lg" />
                    )}
                    {timeslot?.from && <Label variant="time" size="lg" label={timeslot.from} />}
                    {tags?.waitingList && <Label variant="fully-booked" label={__('fullBookedWaitlist')} />}
                    {tags?.wellnessCard && <Label variant="wellness" size="lg" />}
                    {discount.percentage && (
                      <Label variant="smt" label={`-${discount.percentage}%`} icon={{ variant: 'smt' }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <CtaSection />
        </div>
      </Card>
    </>
  );
};

export default ServiceCard;
